// Fontes sem cadastro: gateways/reguladores que publicam mas ainda não pertencem a nenhum
// grupo. O admin da plataforma vincula; a partir daí a ingestão grava o que chegar.

import { useQuery } from "@tanstack/react-query";
import { Link2, PlugZap } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Alert, Badge, Button, EmptyState, Field, Input, Modal, PageHeader, Select, Spinner, Table } from "../../components/ui";
import { api, type Source } from "../../lib/api";
import { useAction, useTree } from "../../lib/hooks";

export function SourcesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["sources"],
    queryFn: () => api.get<{ sources: Source[] }>("/api/admin/sources"),
    refetchInterval: 15_000,
  });
  const [assigning, setAssigning] = useState<Source | null>(null);
  const sources = data?.sources ?? [];

  return (
    <>
      <PageHeader
        title="Fontes sem cadastro"
        description="Gateways que estão publicando mas ainda não foram vinculados a um grupo. As mensagens deles não são gravadas até o vínculo."
      />
      {isLoading ? (
        <Spinner />
      ) : sources.length === 0 ? (
        <EmptyState title="Nenhuma fonte pendente">Todo gateway que publica está vinculado.</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Origem (tópico)</th>
              <th>Mensagens</th>
              <th>Última</th>
              <th className="w-32" />
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={`${s.site}/${s.ug}/${s.reg}`}>
                <td>
                  <code className="font-mono text-xs">{s.ug ? `${s.site}/${s.ug}/${s.reg}` : `${s.site} · gateway ${s.reg}`}</code>
                  <p className="mt-0.5 max-w-md truncate text-xs text-muted" title={s.last_topic}>
                    {s.last_topic}
                  </p>
                </td>
                <td className="tabular-nums">{s.message_count}</td>
                <td className="text-xs text-muted">{new Date(s.last_seen).toLocaleString("pt-BR")}</td>
                <td>
                  {s.ug ? (
                    <Button size="sm" variant="secondary" onClick={() => setAssigning(s)}>
                      <Link2 className="size-3.5" /> Vincular
                    </Button>
                  ) : (
                    <Badge>vincule um regulador da usina</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {assigning && <AssignDialog source={assigning} onClose={() => setAssigning(null)} />}
    </>
  );
}

function AssignDialog({ source, onClose }: { source: Source; onClose: () => void }) {
  const { data: tree } = useTree();
  const { run, busy, error } = useAction([["sources"], ["tree"]]);
  const existingSite = tree?.groups.flatMap((g) => g.sites.map((s) => ({ ...s, groupId: g.id }))).find((s) => s.slug === source.site);
  const [groupId, setGroupId] = useState<number>(existingSite?.groupId ?? tree?.groups[0]?.id ?? 0);
  const [siteName, setSiteName] = useState(existingSite?.name ?? "");
  const [unitName, setUnitName] = useState(source.ug.toUpperCase());
  const [regName, setRegName] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    const ok = await run(() =>
      api.post("/api/admin/sources/assign", {
        groupId,
        site: source.site,
        ug: source.ug,
        reg: source.reg,
        ...(siteName ? { siteName } : {}),
        ...(unitName ? { unitName } : {}),
        ...(regName ? { regulatorName: regName } : {}),
      }),
    );
    if (ok) onClose();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Vincular fonte"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="assign" loading={busy}>
            Vincular
          </Button>
        </>
      }
    >
      <form id="assign" onSubmit={submit} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <p className="flex items-center gap-2 text-sm">
          <PlugZap className="size-4 text-muted" aria-hidden />
          <code className="font-mono">
            {source.site}/{source.ug}/{source.reg}
          </code>
        </p>
        <Field label="Grupo">
          {(id) => (
            <Select id={id} value={groupId} onChange={(e) => setGroupId(Number(e.target.value))} disabled={!!existingSite}>
              {tree?.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Nome da usina" hint={existingSite ? "Usina já cadastrada." : `Identificador: ${source.site}`}>
          {(id) => <Input id={id} value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder={source.site} disabled={!!existingSite} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome da UG">{(id) => <Input id={id} value={unitName} onChange={(e) => setUnitName(e.target.value)} placeholder={source.ug} />}</Field>
          <Field label="Nome do regulador">{(id) => <Input id={id} value={regName} onChange={(e) => setRegName(e.target.value)} placeholder={source.reg} />}</Field>
        </div>
      </form>
    </Modal>
  );
}
